import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { FileText, Clock, CheckCircle2, IndianRupee, TrendingUp, Box, BarChart3, ShoppingCart, Factory, Wrench, Briefcase, ArrowRight, Filter } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { KpiCard } from "@/components/erp/primitives";
import {
  ResponsiveContainer,
  BarChart,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";

const STAT_DEF = [
  { key: "total_indents", label: "Total Indents", icon: FileText, variant: "navy" },
  { key: "pending_approvals", label: "Pending Approvals", icon: Clock, variant: "amber" },
  { key: "approved_orders", label: "Approved Orders", icon: CheckCircle2, variant: "emerald" },
  {
    key: "order_value_sum",
    label: "Order Value (POs)",
    icon: ShoppingCart,
    variant: "blue",
    money: true,
    subRender: (s) => `from ${s?.approved_po_count ?? 0} approved POs · ${s?.value_mode === "gross" ? "Gross (with GST)" : "Basic (ex-GST)"}`,
  },
  { key: "total_purchase_value", label: "Total Purchase Value", icon: IndianRupee, variant: "blue", money: true, subRender: (s) => `GRN inwards · ${s?.value_mode === "gross" ? "Gross (with GST)" : "Basic (ex-GST)"}` },
];

const PG_META = {
  direct_purchase: { label: "Direct Purchase", icon: Factory, color: "#1D2951", tint: "from-slate-50 to-white", iconBg: "bg-slate-100 text-[#1D2951]" },
  indirect_purchase: { label: "Indirect Purchase", icon: Briefcase, color: "#0369A1", tint: "from-sky-50/70 to-white", iconBg: "bg-sky-100 text-sky-700" },
  labour_work: { label: "Labour Work", icon: Wrench, color: "#0891B2", tint: "from-cyan-50/70 to-white", iconBg: "bg-cyan-100 text-cyan-700" },
};

// Space-Cadet palette for bar shades
const PALETTE = ["#1D2951", "#2B3A67", "#3D5A80", "#5B7DB1", "#8AA4C9", "#B6CCE3", "#0F766E", "#B45309", "#9F1239"];

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// --- Date helpers ----------------------------------------------------------
const _ymd = (d) => d.toISOString().slice(0, 10);
const _firstOfMonth = () => { const t = new Date(); return _ymd(new Date(t.getFullYear(), t.getMonth(), 1)); };
const _todayISO = () => _ymd(new Date());

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const _monthRange = (year, monthIdx /* 0..11 */) => {
  const first = new Date(year, monthIdx, 1);
  const last = new Date(year, monthIdx + 1, 0);
  return { from: _ymd(first), to: _ymd(last) };
};

const _applyPreset = (key) => {  // eslint-disable-line no-unused-vars
  const t = new Date();
  if (key === "this_month") return { from: _firstOfMonth(), to: _todayISO() };
  if (key === "last_month") {
    const first = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    const last = new Date(t.getFullYear(), t.getMonth(), 0);
    return { from: _ymd(first), to: _ymd(last) };
  }
  if (key === "last_30") {
    const back = new Date(t); back.setDate(t.getDate() - 30);
    return { from: _ymd(back), to: _todayISO() };
  }
  if (key === "fy_so_far") {
    // Indian FY = Apr 1 → Mar 31
    const fyStartYear = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
    return { from: _ymd(new Date(fyStartYear, 3, 1)), to: _todayISO() };
  }
  if (key === "all_time") return { from: "1970-01-01", to: _todayISO() };
  return { from: _firstOfMonth(), to: _todayISO() };
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [grnRows, setGrnRows] = useState([]);
  const [byCC, setByCC] = useState([]);
  // Budget utilization rows from /budget-sheet/utilization — powers the 75%/100%
  // threshold lines on the cost-center chart.
  const [budgetUtil, setBudgetUtil] = useState([]);
  const [byCat, setByCat] = useState([]);
  const [pgData, setPgData] = useState({ total: 0, groups: [] });
  const [pgFilter, setPgFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [graphCCFilter, setGraphCCFilter] = useState(""); // "" = no filter (show all)
  const [rmTypeFilter, setRmTypeFilter] = useState(""); // "" = all grades, "__by_type__" = group by type, "<name>" = filter to that type
  const [rmTypes, setRmTypes] = useState([]);
  const [companyFilter, setCompanyFilter] = useState(""); // "" = All companies
  const [companyOptions, setCompanyOptions] = useState([]);
  // Value mode — "basic" (pre-GST) or "gross" (with GST). Drives every ₹ figure.
  const [valueMode, setValueMode] = useState("basic");
  // Date range — defaults to current month. Auto-rolls over when calendar month changes.
  const [dateRange, setDateRange] = useState({ from: _firstOfMonth(), to: _todayISO() });
  const [presetKey, setPresetKey] = useState("this_month");
  // Month chips — pick a single calendar month. `null` = none picked (use preset or custom).
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const navigate = useNavigate();

  const pickMonth = (year, monthIdx) => {
    setSelectedMonth(monthIdx);
    setSelectedYear(year);
    setDateRange(_monthRange(year, monthIdx));
    setPresetKey("custom");
  };
  // Quick year selector — last 4 years + current
  const _yearOpts = (() => {
    const cy = new Date().getFullYear();
    return [cy, cy - 1, cy - 2, cy - 3];
  })();

  // Re-fetch top-level dashboard data whenever company or date range changes
  useEffect(() => {
    const params = { value_mode: valueMode };
    if (companyFilter) params.company = companyFilter;
    if (dateRange.from) params.from_date = dateRange.from;
    if (dateRange.to) params.to_date = dateRange.to;
    const cfg = { params };
    api.get("/dashboard/stats", cfg).then((r) => setStats(r.data));
    api.get("/dashboard/recent-grns", cfg).then((r) => setGrnRows(r.data || []));
    api.get("/dashboard/by-cost-center", cfg).then((r) => setByCC(r.data || []));
    api.get("/dashboard/purchase-groups", cfg).then((r) => setPgData(r.data || { total: 0, groups: [] }));
    // Fetch live budget utilization for the current FY. Powers the red (100%)
    // and orange (75%) threshold lines overlaid on the cost-center chart.
    api.get("/budget-sheet/utilization").then((r) => setBudgetUtil(r.data?.rows || [])).catch(() => setBudgetUtil([]));
  }, [companyFilter, dateRange.from, dateRange.to, valueMode]);

  useEffect(() => {
    api.get("/rm-types").then((r) => setRmTypes((r.data || []).map((x) => x.name))).catch(() => {});
    api.get("/dashboard/companies").then((r) => setCompanyOptions(r.data || [])).catch(() => {});
  }, []);

  const isRawMaterial = (graphCCFilter || "").trim().toLowerCase() === "raw material";

  // Reset the RM-Type sub-filter whenever the cost-center selection changes
  useEffect(() => { setRmTypeFilter(""); }, [graphCCFilter]);

  // Re-fetch the Category graph whenever the Cost-Center / RM-Type / company / date filters change
  useEffect(() => {
    const params = { value_mode: valueMode };
    if (graphCCFilter) params.cost_center = graphCCFilter;
    if (isRawMaterial && rmTypeFilter) params.rm_type = rmTypeFilter;
    if (companyFilter) params.company = companyFilter;
    if (dateRange.from) params.from_date = dateRange.from;
    if (dateRange.to) params.to_date = dateRange.to;
    api.get("/dashboard/by-category", { params }).then((r) => setByCat(r.data || []));
  }, [graphCCFilter, rmTypeFilter, isRawMaterial, companyFilter, dateRange.from, dateRange.to, valueMode]);

  // Cost-center options for the correlation dropdown — drawn from the live by-cost-center series
  const costCenterOptions = useMemo(() => {
    return byCC.map((r) => r.name).filter(Boolean);
  }, [byCC]);

  // Distinct categories drawn from the grn rows themselves so the dropdown matches what's visible
  const categoryOptions = useMemo(() => {
    const set = new Set();
    grnRows.forEach((r) => set.add((r.category || "").trim() || "Uncategorized"));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [grnRows]);

  const filteredGrnRows = useMemo(() => {
    return grnRows.filter((r) => {
      if (pgFilter !== "all" && r.purchase_group !== pgFilter) return false;
      if (catFilter !== "all") {
        const rowCat = (r.category || "").trim() || "Uncategorized";
        if (rowCat !== catFilter) return false;
      }
      return true;
    });
  }, [grnRows, pgFilter, catFilter]);

  // Guarantee the 3 canonical groups appear even at zero
  const canonicalPG = ["direct_purchase", "indirect_purchase", "labour_work"].map((key) => {
    const found = (pgData.groups || []).find((g) => g.key === key);
    return { key, label: PG_META[key].label, value: Number(found?.value || 0) };
  });

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Operations · Today</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Live procurement metrics · inward (GRN) data powers totals, graphs and drill-downs · showing <span className="font-semibold text-slate-700">{valueMode === "gross" ? "Gross (with GST)" : "Basic (ex-GST)"}</span> values.
          </p>
        </div>
        <div className="inline-flex items-center gap-3 flex-wrap">
          {/* Value Mode toggle — Basic (ex-GST) or Gross (with GST). Drives every ₹ on this page. */}
          <div className="inline-flex items-center bg-white border border-slate-200 rounded-lg p-0.5 shadow-sm" data-testid="dashboard-value-mode">
            <button
              type="button"
              onClick={() => setValueMode("basic")}
              data-testid="dashboard-value-mode-basic"
              className={`text-xs font-semibold uppercase tracking-widest px-3.5 py-1.5 rounded-md transition-colors ${valueMode === "basic" ? "bg-[#1D2951] text-white shadow-sm" : "text-slate-600 hover:text-[#1D2951]"}`}
              title="Basic value (excludes GST)"
            >
              Basic
            </button>
            <button
              type="button"
              onClick={() => setValueMode("gross")}
              data-testid="dashboard-value-mode-gross"
              className={`text-xs font-semibold uppercase tracking-widest px-3.5 py-1.5 rounded-md transition-colors ${valueMode === "gross" ? "bg-[#1D2951] text-white shadow-sm" : "text-slate-600 hover:text-[#1D2951]"}`}
              title="Gross value (includes GST)"
            >
              Gross
            </button>
          </div>
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Company</span>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              data-testid="dashboard-company-filter"
              className="bg-transparent text-sm font-semibold text-slate-900 focus:outline-none min-w-[180px]"
            >
              <option value="">All Companies</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {/* Monthly dropdown — pick a calendar month. Custom range still
              supported via the date inputs to the right. */}
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5 flex-wrap" data-testid="dashboard-month-block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Monthly</span>
            <select
              value={selectedMonth}
              onChange={(e) => { const m = Number(e.target.value); pickMonth(selectedYear, m); }}
              data-testid="dashboard-month"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none border-l border-slate-200 pl-2"
            >
              {MONTH_NAMES.map((m, idx) => (
                <option key={m} value={idx}>{m}</option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => { const y = Number(e.target.value); setSelectedYear(y); pickMonth(y, selectedMonth); }}
              data-testid="dashboard-year"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none border-l border-slate-200 pl-2"
            >
              {_yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {/* Custom Date Range (still available — picking a date here flips into custom mode) */}
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Custom</span>
            <input
              type="date"
              value={dateRange.from || ""}
              onChange={(e) => { setPresetKey("custom"); setSelectedMonth(-1); setDateRange((r) => ({ ...r, from: e.target.value })); }}
              data-testid="dashboard-range-from"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none"
            />
            <span className="text-slate-400 text-xs">→</span>
            <input
              type="date"
              value={dateRange.to || ""}
              onChange={(e) => { setPresetKey("custom"); setSelectedMonth(-1); setDateRange((r) => ({ ...r, to: e.target.value })); }}
              data-testid="dashboard-range-to"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none"
            />
          </div>
          <button
            onClick={() => navigate("/indents")}
            data-testid="new-indent-btn"
            className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2"
          >
          <FileText size={14} /> New Indent
        </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {STAT_DEF.map((s) => {
          const v = stats[s.key] ?? 0;
          const displayValue = s.money ? fmtINR(v) : v;
          return (
            <KpiCard
              key={s.key}
              testid={`stat-${s.key}`}
              title={s.label}
              value={displayValue}
              icon={s.icon}
              variant={s.variant}
              subtitle={s.subRender ? s.subRender(stats) : (s.sub || <span className="inline-flex items-center gap-1"><TrendingUp size={11} /> live</span>)}
            />
          );
        })}
      </div>

      {/* Purchase Group breakdown */}
      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Purchase Group Breakdown</div>
            <div className="text-xs text-slate-500">Sums up to Total Purchase Value (₹{Number(pgData.total || 0).toLocaleString("en-IN")}) · tap to drill down</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {canonicalPG.map((g) => {
            const meta = PG_META[g.key];
            const Icon = meta.icon;
            const pct = pgData.total > 0 ? (g.value / pgData.total) * 100 : 0;
            return (
              <button
                key={g.key}
                onClick={() => navigate(`/dashboard/purchase-group/${g.key}`)}
                data-testid={`pg-card-${g.key}`}
                className={`text-left rounded-xl p-5 border border-slate-200 bg-gradient-to-b ${meta.tint} hover:shadow-md hover:border-[#1D2951]/40 transition-all group`}
              >
                <div className="flex items-center justify-between">
                  <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${meta.iconBg}`}>
                    <Icon size={17} />
                  </span>
                  <ArrowRight size={14} className="text-slate-400 group-hover:text-[#1D2951] group-hover:translate-x-0.5 transition-transform" />
                </div>
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{g.label}</div>
                  <div className="text-2xl font-semibold font-mono text-slate-900 mt-1">{fmtINR(g.value)}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-200/60 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                    </div>
                    <span className="text-[11px] font-mono text-slate-500 w-10 text-right">{pct.toFixed(0)}%</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Graphs */}
      <div>
        <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Spend Analysis</div>
            <div className="text-xs text-slate-500">
              {graphCCFilter
                ? `Categories filtered by Cost Center: ${graphCCFilter}`
                : "Pick a Cost Center to drill into the categories that contribute to its spend"}
            </div>
          </div>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-slate-500" />
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Cost Center By</span>
            <select
              value={graphCCFilter}
              onChange={(e) => setGraphCCFilter(e.target.value)}
              data-testid="dashboard-graph-cc-filter"
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] min-w-[200px]"
            >
              <option value="">All Cost Centers</option>
              {costCenterOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            {isRawMaterial && (
              <>
                <button
                  type="button"
                  onClick={() => setRmTypeFilter(rmTypeFilter === "__by_type__" ? "" : "__by_type__")}
                  data-testid="dashboard-rm-type-toggle"
                  className={`text-[11px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-md border transition-colors ${rmTypeFilter === "__by_type__" ? "bg-[#1D2951] text-white border-[#1D2951]" : "bg-white text-[#1D2951] border-[#1D2951]/30 hover:bg-[#1D2951]/5"}`}
                >
                  RM Type
                </button>
                <select
                  value={rmTypeFilter === "__by_type__" ? "" : rmTypeFilter}
                  onChange={(e) => setRmTypeFilter(e.target.value)}
                  data-testid="dashboard-rm-type-filter"
                  className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] min-w-[160px]"
                >
                  <option value="">All Grades</option>
                  {rmTypes.map((t) => (
                    <option key={t} value={t}>Type: {t}</option>
                  ))}
                </select>
              </>
            )}
            {graphCCFilter && (
              <button
                onClick={() => { setGraphCCFilter(""); setRmTypeFilter(""); }}
                data-testid="dashboard-graph-cc-clear"
                className="text-xs font-semibold text-[#1D2951] hover:underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="space-y-5">
          <ChartCard
            title="Spend by Cost Center"
            subtitle="Inward (GRN) value · red = 100% budget · orange = 75%"
            testid="chart-cost-center"
            data={byCC}
            solidColor="#1D2951"
            budgetUtil={budgetUtil}
          />
          <ChartCard
            title={
              isRawMaterial
                ? rmTypeFilter === "__by_type__"
                  ? "Spend by RM Type"
                  : rmTypeFilter
                  ? `Spend by Grade — ${rmTypeFilter}`
                  : "Spend by Grade — Raw Material"
                : graphCCFilter
                ? `Spend by Item Category — ${graphCCFilter}`
                : "Spend by Item Category"
            }
            subtitle={
              isRawMaterial
                ? rmTypeFilter === "__by_type__"
                  ? "Inward (GRN) value · grouped by RM Type"
                  : rmTypeFilter
                  ? `Grades within ${rmTypeFilter} · highest first`
                  : "All Raw Material grades · highest first"
                : graphCCFilter
                ? `Categories within ${graphCCFilter} · highest first`
                : "Inward (GRN) value · highest first"
            }
            testid="chart-category"
            data={byCat}
            perBarColor
          />
        </div>
      </div>

      {/* Cost Center + Category Log (sourced from GRN) */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Cost Center &amp; Category Log</div>
            <div className="text-xs text-slate-500">Live from inwarded (GRN) data · use the Purchase Group filter to narrow down</div>
          </div>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-slate-500" />
            <select
              value={pgFilter}
              onChange={(e) => setPgFilter(e.target.value)}
              data-testid="dashboard-pg-filter"
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
            >
              <option value="all">All Purchase Groups</option>
              <option value="direct_purchase">Direct Purchase</option>
              <option value="indirect_purchase">Indirect Purchase</option>
              <option value="labour_work">Labour Work</option>
            </select>
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              data-testid="dashboard-category-filter"
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
            >
              <option value="all">All Categories</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <th className="text-left px-6 py-3">Date</th>
                <th className="text-left px-6 py-3">GRN No.</th>
                <th className="text-left px-6 py-3">PO No.</th>
                <th className="text-left px-6 py-3">Cost Center</th>
                <th className="text-left px-6 py-3">Category</th>
                <th className="text-left px-6 py-3">Purchase Group</th>
                <th className="text-right px-6 py-3">Actual Value</th>
                <th className="text-right px-6 py-3">Gross Value</th>
              </tr>
            </thead>
            <tbody data-testid="dashboard-cc-cat-list">
              {filteredGrnRows.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-slate-400">
                    <Box size={28} className="mx-auto mb-2 opacity-40" />
                    {grnRows.length === 0 ? "No GRN data yet" : "No rows match the selected filters"}
                  </td>
                </tr>
              )}
              {filteredGrnRows.map((r, idx) => (
                <tr key={`${r.id}-${idx}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-3 font-mono text-xs">{r.date || "—"}</td>
                  <td className="px-6 py-3 font-mono text-xs font-semibold text-emerald-700">{r.grn_number || "—"}</td>
                  <td className="px-6 py-3 font-mono text-xs font-semibold text-[#1D2951]" data-testid={`dashboard-row-po-${r.grn_number || idx}`}>{r.po_number || "—"}</td>
                  <td className="px-6 py-3">{r.cost_center || "—"}</td>
                  <td className="px-6 py-3">{r.category || "—"}</td>
                  <td className="px-6 py-3">
                    {r.purchase_group ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#1D2951]/10 text-[#1D2951]">
                        {r.purchase_group_label}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-6 py-3 text-right font-mono">₹{Number(r.actual_value).toLocaleString("en-IN")}</td>
                  <td className="px-6 py-3 text-right font-mono font-semibold">₹{Number(r.gross_value).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, testid, data, solidColor, perBarColor, budgetUtil }) {
  // Merge budget allocation into each bar row so the two threshold lines
  // (75% + 100%) can be drawn per-cost-centre alongside the spend bar.
  const budgetByCC = React.useMemo(() => {
    const m = new Map();
    (budgetUtil || []).forEach((r) => m.set(r.cost_center, r));
    return m;
  }, [budgetUtil]);
  const chartData = React.useMemo(() => (data || []).map((row) => {
    const b = budgetByCC.get(row.name);
    return b && b.allocated > 0
      ? { ...row, threshold_100: b.allocated, threshold_75: b.threshold_75 }
      : { ...row };
  }), [data, budgetByCC]);
  const hasBudget = (budgetUtil || []).some((r) => r.allocated > 0);

  // Build alert list — cost centres that have crossed either threshold.
  const breaches = React.useMemo(() => {
    if (!hasBudget) return [];
    return (data || [])
      .map((row) => {
        const b = budgetByCC.get(row.name);
        if (!b || !b.allocated) return null;
        const pct = (Number(row.value || 0) / b.allocated) * 100;
        if (pct < 75) return null;
        return { name: row.name, spent: row.value, allocated: b.allocated, pct: Math.round(pct) };
      })
      .filter(Boolean)
      .sort((a, b) => b.pct - a.pct);
  }, [data, budgetByCC, hasBudget]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 size={16} className="text-[#1D2951]" /> {title}
          </div>
          <div className="text-xs text-slate-500">{subtitle}</div>
        </div>
        {hasBudget && (
          <div className="text-[10px] inline-flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md px-2 py-1">
            <span className="inline-flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-rose-600" style={{ borderTop: "2px dashed #E11D48" }} /> 100%</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-4 h-0.5" style={{ borderTop: "2px dashed #F59E0B" }} /> 75%</span>
          </div>
        )}
      </div>

      {/* Budget alert banner — visible only when at least one CC crossed 75% or 100% */}
      {breaches.length > 0 && (
        <div className="mb-3 space-y-1" data-testid={`${testid}-alerts`}>
          {breaches.map((b) => (
            <div
              key={b.name}
              className={`text-[11px] rounded-md px-2.5 py-1.5 border font-medium ${
                b.pct >= 100
                  ? "bg-rose-50 border-rose-200 text-rose-800"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              }`}
            >
              {b.pct >= 100 ? "🔴" : "🟠"} <b>{b.name}</b> — spent{" "}
              <span className="font-mono">{fmtINR(b.spent)}</span> of{" "}
              <span className="font-mono">{fmtINR(b.allocated)}</span>{" "}
              <span className="font-semibold">({b.pct}%)</span>{" "}
              {b.pct >= 100 ? "· 100% budget exceeded" : "· 75% threshold crossed"}
            </div>
          ))}
        </div>
      )}

      {chartData.length === 0 ? (
        <EmptyChart message="No inward (GRN) data yet" />
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 16, left: 0, bottom: 32 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: "#475569" }}
              interval={0}
              angle={-22}
              textAnchor="end"
              height={54}
            />
            <YAxis tick={{ fontSize: 11, fill: "#475569" }} tickFormatter={(v) => fmtINR(v)} />
            <Tooltip
              formatter={(v, name) => {
                if (name === "threshold_75") return [fmtINR(v), "75% Budget"];
                if (name === "threshold_100") return [fmtINR(v), "100% Budget"];
                return fmtINR(v);
              }}
              contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: "#1D2951" }}
              labelStyle={{ fontWeight: 600, color: "#0F172A" }}
            />
            <Bar dataKey="value" radius={[6, 6, 0, 0]} {...(solidColor ? { fill: solidColor } : {})}>
              {perBarColor && chartData.map((_, idx) => (
                <Cell key={idx} fill={PALETTE[idx % PALETTE.length]} />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                formatter={(v) => fmtINR(v)}
                fill="#0F172A"
                fontSize={11}
                fontWeight={600}
              />
            </Bar>
            {/* Threshold lines. `connectNulls={false}` so CCs without a budget
                don't distort the trace. Dashed strokes read as reference lines
                rather than data. */}
            {hasBudget && (
              <>
                <Line
                  type="monotone"
                  dataKey="threshold_75"
                  stroke="#F59E0B"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{ r: 3, fill: "#F59E0B", stroke: "#F59E0B" }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="threshold_100"
                  stroke="#E11D48"
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                  dot={{ r: 3, fill: "#E11D48", stroke: "#E11D48" }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-700",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${map[status] || "bg-slate-100 text-slate-700"}`}>
      {status}
    </span>
  );
}

function EmptyChart({ message }) {
  return (
    <div className="h-[300px] flex flex-col items-center justify-center text-slate-400 text-sm">
      <Box size={28} className="mb-2 opacity-40" />
      {message}
    </div>
  );
}
